import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  DATE_PRESETS,
  entryValue,
  formatHours,
  formatMoney,
  groupTime,
  isBillableEntry,
  percent,
  presetRange,
  TimeGroupBy,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  MiniBar,
  Note,
  NUM,
  ReportPage,
  Segmented,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllTime } from "../useReportData";

const GROUPS: { value: TimeGroupBy; label: string }[] = [
  { value: "person", label: "Team member" },
  { value: "client", label: "Client" },
  { value: "workType", label: "Work type" },
];

export const TimeReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters("thisMonth");
  const { filters } = state;
  const [by, setBy] = useViewExtra<TimeGroupBy>(
    "groupBy",
    access.firmWide ? "person" : "client",
    GROUPS,
    "Group by",
  );
  const range = presetRange(filters.preset);
  const time = useAllTime({
    from: range?.from ?? null,
    to: range?.to ?? null,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    workTypeIds: filters.workTypeIds,
  });
  const rows = useMemo(() => groupTime(time.data, by), [time.data, by]);
  const sum = (pick: (r: typeof rows[number]) => number) =>
    rows.reduce((s, r) => s + pick(r), 0);
  const hours = sum((r) => r.hours);
  const billable = sum((r) => r.billable);
  const nonBillable = sum((r) => r.nonBillable);
  const billed = sum((r) => r.billed);
  const unbilled = sum((r) => r.unbilled);
  const unbilledValue = time.data
    .filter((e) => !e.isBilled && isBillableEntry(e))
    .reduce((s, e) => s + entryValue(e), 0);
  const value = sum((r) => r.value);
  const showMoney = access.billing;
  const firstCol = GROUPS.find((g) => g.value === by)?.label ?? "";
  const v = (x: React.ReactNode) => (time.isLoading ? "…" : x);

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        [firstCol]: r.name,
        Hours: Math.round(r.hours * 100) / 100,
        "Billable hours": Math.round(r.billable * 100) / 100,
        "Non-billable hours": Math.round(r.nonBillable * 100) / 100,
        "Billed hours": Math.round(r.billed * 100) / 100,
        "Not billed yet (hours)": Math.round(r.unbilled * 100) / 100,
        ...(showMoney ? { "Billable value": Math.round(r.value) } : {}),
      })),
      "Time report",
    );

  const tableName = `Time by ${firstCol.toLowerCase()}`;
  const shownRows = useFocusRows(tableName, rows, (r) => r.id);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Time report",
      path: "/reports/time",
      filters: filtersText([
        `Date: ${DATE_PRESETS.find((p) => p.value === filters.preset)?.label}`,
        !access.firmWide && "Only your own time",
      ]),
      kpis: [
        { label: "Total hours", value: formatHours(hours) },
        { label: "Billable hours", value: formatHours(billable) },
        { label: "Non-billable hours", value: formatHours(nonBillable) },
        { label: "Not billed yet (hours)", value: formatHours(unbilled) },
        ...(showMoney
          ? [
              {
                label: "Not billed yet (value)",
                value: formatMoney(unbilledValue),
              },
            ]
          : []),
      ],
      tables: [
        askTable(
          tableName,
          rows,
          [
            {
              key: "name",
              label: firstCol,
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "hours",
              label: "Hours",
              kind: "hours",
              value: (r) => r.hours,
            },
            {
              key: "billable",
              label: "Billable hours",
              kind: "hours",
              value: (r) => r.billable,
            },
            {
              key: "nonBillable",
              label: "Non-billable hours",
              kind: "hours",
              value: (r) => r.nonBillable,
            },
            {
              key: "billed",
              label: "Billed hours",
              kind: "hours",
              value: (r) => r.billed,
            },
            {
              key: "unbilled",
              label: "Not billed yet (hours)",
              kind: "hours",
              value: (r) => r.unbilled,
            },
            ...(showMoney
              ? [
                  {
                    key: "value",
                    label: "Billable value",
                    kind: "money" as const,
                    value: (r: typeof rows[number]) => r.value,
                  },
                ]
              : []),
          ],
          (r) => r.id,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, filters.preset, showMoney, firstCol],
  );

  return (
    <ReportPage
      title={"Time report"}
      description={
        "Hours tracked by your team: how much is billable, and how much of that is already on an invoice."
      }
      onExport={rows.length ? onExport : undefined}
      ask={time.isLoading ? undefined : ask}
      askSuggestions={[
        "Who logged the most non-billable time?",
        "What share of the hours is billable?",
        "Who has the most time not billed yet?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {time.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Total hours"}
          value={v(formatHours(hours))}
          note={`${time.data.length} time entries`}
        />
        <Kpi
          label={"Billable"}
          value={v(formatHours(billable))}
          note={`${percent(billable, hours)}% of all time · ${formatHours(
            billed,
          )} billed`}
          tone={"green"}
        />
        <Kpi
          label={"Non-billable"}
          value={v(formatHours(nonBillable))}
          note={"Not charged to clients"}
        />
        <Kpi
          label={"Not billed yet"}
          value={v(formatHours(unbilled))}
          note={
            showMoney
              ? `Billable, not on an invoice · ${formatMoney(unbilledValue)}`
              : "Billable, not on an invoice"
          }
          tone={"amber"}
        />
      </KpiGrid>

      <Card
        title={"Hours by"}
        right={
          <div className={"flex flex-wrap items-center gap-3.5"}>
            <Segmented
              label={"Group by"}
              value={by}
              onChange={setBy}
              options={GROUPS}
            />
            <Legend
              items={[
                { label: "Billable", color: "#6B4EFF" },
                { label: "Non-billable", color: "#C9CEDA" },
              ]}
            />
          </div>
        }>
        {time.isLoading ? (
          <Loading />
        ) : time.isError ? null : !rows.length ? (
          <Empty text={"No time was tracked in this period."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  {firstCol}
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Hours
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Non-billable
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Billable
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Billed
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Not billed yet
                </th>
                <th scope={"col"} className={TH}>
                  Billable share
                </th>
                {showMoney && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Billable value
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const pct = percent(r.billable, r.hours);
                return (
                  <tr key={r.id || r.name}>
                    <td className={`${TD} font-bold`}>{r.name}</td>
                    <td className={`${TD} ${NUM} font-extrabold`}>
                      {formatHours(r.hours)}
                    </td>
                    <td className={`${TD} ${NUM}`}>
                      {formatHours(r.nonBillable)}
                    </td>
                    <td className={`${TD} ${NUM}`}>
                      {formatHours(r.billable)}
                    </td>
                    <td className={`${TD} ${NUM}`}>{formatHours(r.billed)}</td>
                    <td className={`${TD} ${NUM}`}>
                      {formatHours(r.unbilled)}
                    </td>
                    <td className={TD}>
                      <span className={"inline-block w-10 font-bold"}>
                        {pct}%
                      </span>
                      <MiniBar pct={pct} color={"#6B4EFF"} width={150} />
                    </td>
                    {showMoney && (
                      <td className={`${TD} ${NUM}`}>{formatMoney(r.value)}</td>
                    )}
                  </tr>
                );
              })}
              <tr>
                <td className={`${TD} font-extrabold`}>Total</td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(hours)}
                </td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(nonBillable)}
                </td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(billable)}
                </td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(billed)}
                </td>
                <td className={`${TD} ${NUM} font-extrabold`}>
                  {formatHours(unbilled)}
                </td>
                <td className={`${TD} font-extrabold`}>
                  {percent(billable, hours)}%
                </td>
                {showMoney && (
                  <td className={`${TD} ${NUM} font-extrabold`}>
                    {formatMoney(value)}
                  </td>
                )}
              </tr>
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Time is billable unless it was switched to non-billable (or its work
        type is non-billable by default). Values are for billable time, at each
        person&apos;s current hourly rate.
      </Note>
    </ReportPage>
  );
};
